<template>
  <b-container fluid class="h-100">
    <b-row align-h="center" class="h-100 py-5">
      <b-col md="6" lg="3" style="min-width: 400px">
        <div class="d-flex mb-4">
          <landing-logo size="60px" />
        </div>
        <div id="lu-name" class="h5 display-4 mt-1 mb-3 font-weight-bold">Sign up</div>

        <DuplicateEmailError v-if="errorAuth === 'DUPLICATE_EMAIL'" :auth-type="'USER_PASSS'" />
        <MissingDataError v-if="errorAuth === 'MISSING_LASTNAME'" />
        <ButtonGoogleAuth v-if="$_isFeatureEnabled('google_auth')" />
        <DuplicateEmailError v-if="isShowAlert" :auth-type="'GOOGLE'" />
        <!-- Name -->
        <b-form-group label="Name">
          <div class="text-dark mb-u2">
            Your name in the following form will be displayed on the certificate.
          </div>
          <b-form-input
            id="name"
            v-model="$v.form.firstname.$model"
            type="text"
            placeholder="Enter your name"
            :state="$v.form.firstname.$anyError ? false : null"
          />
          <b-form-invalid-feedback> Name is required </b-form-invalid-feedback>
        </b-form-group>

        <!-- Surname -->
        <b-form-group label="Surname">
          <b-form-input
            id="surname"
            v-model="$v.form.lastname.$model"
            type="text"
            placeholder="Enter your surname"
            :state="$v.form.lastname.$anyError ? false : null"
          />
          <b-form-invalid-feedback> Surname is required. </b-form-invalid-feedback>
        </b-form-group>

        <!-- preview certificate -->
        <b-button
          class="w-100 mb-3"
          variant="outline-primary "
          :disabled="$v.form.firstname.$error || $v.form.lastname.$error"
          @click="isShowPreview = true"
        >
          <span> Preview Certificate Sample </span>
        </b-button>

        <!-- Email -->
        <b-form-group label="Email">
          <b-form-input
            id="email"
            v-model="$v.form.email.$model"
            type="text"
            placeholder="Enter your email"
            :formatter="emailFormatter"
            :disabled="hasFirebaseUser"
            :state="$v.form.email.$anyError ? false : null"
          />
          <b-form-invalid-feedback v-if="!$v.form.email.required">
            Email is required.
          </b-form-invalid-feedback>
          <b-form-invalid-feedback v-if="!$v.form.email.email">
            Email is invalid format.
          </b-form-invalid-feedback>
        </b-form-group>

        <!-- Confirm Email -->
        <b-form-group label="Confirm Email">
          <b-form-input
            id="confirm-email"
            v-model="$v.confirmEmail.$model"
            type="text"
            placeholder="Enter your email"
            :formatter="emailFormatter"
            :disabled="hasFirebaseUser"
            :state="$v.confirmEmail.$anyError ? false : null"
          />
          <b-form-invalid-feedback v-if="!$v.confirmEmail.required">
            Email is required.
          </b-form-invalid-feedback>
          <b-form-invalid-feedback v-if="!$v.confirmEmail.email">
            Email is invalid format.
          </b-form-invalid-feedback>
        </b-form-group>

        <!-- Password -->
        <b-form-group label="Password">
          <b-form-input
            id="password"
            v-model="$v.form.password.$model"
            :type="`${isShowPassword ? 'text' : 'password'}`"
            placeholder="Enter your password"
            :disabled="hasFirebaseUser"
            :state="$v.form.password.$anyError ? false : null"
            disable-value-track
          />
          <div v-if="!$v.form.password.$anyDirty" class="mt-1 text-dark small">
            <span>Password must be at least 6 characters.</span>
          </div>
          <b-form-invalid-feedback v-if="!$v.form.password.required">
            Password is required.
          </b-form-invalid-feedback>
          <b-form-invalid-feedback v-if="!$v.form.password.minLength">
            Password is too short.
          </b-form-invalid-feedback>
        </b-form-group>

        <!-- Show password -->
        <div>
          <b-form-checkbox
            v-model="isShowPassword"
            aria-describedby="flavours"
            aria-controls="flavours"
          >
            Show password
          </b-form-checkbox>
        </div>

        <div class="mt-4">
          <b-button type="submit" class="w-100" variant="first" @click="signUp">
            <span> Sign up </span>
          </b-button>
        </div>

        <div class="mt-4">
          <span class="text-dark">
            Already have an account?
            <router-link :to="signInPage">Sign in</router-link>
          </span>
        </div>
      </b-col>
      <modal-certificate-example
        ref="preview-certificate-modal"
        v-model="isShowPreview"
        :firstname="form.firstname"
        :lastname="form.lastname"
      />
    </b-row>
  </b-container>
</template>

<script>
import { getAuth } from 'firebase/auth';
import { required, minLength, email } from 'vuelidate/lib/validators';
import Utility from '@/models/Utility';
import { notificationMixin } from '@/mixins';
import { optimizelyMixin } from '@/mixins/optimizelyMixin';

import { useAuth } from '@/composables';
import { amplitudeMixin } from '@/mixins/amplitudeMixin';
import LandingLogo from '@/components/layout/LandingLogo.vue';
import { useInputFormatter } from '@/composables/useInputFormatter';

import ButtonGoogleAuth from './components/ButtonGoogleAuth.vue';
import ModalCertificateExample from './../../views/Profile/components/ModalCertificateExample.vue';
import MissingDataError from './components/MissingDataError.vue';
import DuplicateEmailError from './components/DuplicateEmailError.vue';

const { logout } = useAuth();
const { email: emailFormatter } = useInputFormatter();

export default {
  components: {
    LandingLogo,
    ModalCertificateExample,
    ButtonGoogleAuth,

    MissingDataError,
    DuplicateEmailError
  },

  mixins: [notificationMixin, amplitudeMixin, optimizelyMixin],

  data() {
    return {
      form: {
        firstname: '',
        lastname: '',
        email: '',
        password: ''
      },
      confirmEmail: '',
      isShowPassword: false,
      hasFirebaseUser: false,
      isShowPreview: false,
      isShowAlert: false
    };
  },

  computed: {
    signInPage() {
      const queryWithoutError = { ...this.$route.query };
      delete queryWithoutError.error;
      return { name: 'SignIn', query: queryWithoutError };
    },
    errorAuth() {
      return this.$route.query.error;
    }
  },

  validations() {
    return {
      form: {
        firstname: { required },
        lastname: { required },
        email: { required, email },
        password: { required, minLength: minLength(6) }
      },
      confirmEmail: {
        required,
        email
      }
    };
  },

  async mounted() {
    this.hasFirebaseUser = false;

    const temporaryPassword = sessionStorage.getItem('kmutt4life__temp-password');

    if (!temporaryPassword) return;

    const firebaseUser = getAuth().currentUser;

    if (firebaseUser) {
      this.hasFirebaseUser = true;

      const updatedForm = {};

      if (firebaseUser.email) {
        this.$set(updatedForm, 'email', firebaseUser.email);
        this.confirmEmail = firebaseUser.email;
      } else {
        // Microsoft provider didn't have email field at the root firebase isntance
        if (firebaseUser.providerData && firebaseUser.providerData.length) {
          const providerData = firebaseUser.providerData[0];

          if (providerData.email) {
            this.$set(updatedForm, 'email', providerData.email);
            this.confirmEmail = firebaseUser.email;
          }
        }
      }

      if (firebaseUser.displayName) {
        const splittedDisplayName = firebaseUser.displayName.split(' ');

        const firstname = splittedDisplayName.splice(0, 1)[0];

        const lastname = splittedDisplayName.join(' ');

        this.$set(updatedForm, 'firstname', firstname);

        this.$set(updatedForm, 'lastname', lastname);
      }

      this.$set(updatedForm, 'password', temporaryPassword);

      const authToken = await firebaseUser.getIdToken();

      if (authToken) {
        this.$set(updatedForm, 'token', authToken);
      }

      this.form = {
        ...this.form,
        ...updatedForm
      };
    }
  },

  methods: {
    emailFormatter,

    async signUp() {
      this.isShowAlert = false;

      if (this.$route.query.error) {
        this.$router.replace({ query: null });
      }

      this.$v.$touch();

      if (this.$v.$invalid) {
        return;
      }

      if (this.form.email !== this.confirmEmail) {
        return this.notiErrorDefault('email and confirm email do not match.');
      }

      const payload = Object.assign({}, this.form);

      payload.username = payload.email;

      if (!this.hasFirebaseUser) {
        const {
          response: { data }
        } = await Utility.api().validateSignUp({
          firstName: payload.firstname,
          lastName: payload.lastname,
          email: payload.email
        });

        const { valid = false } = data;

        if (!valid) {
          const { errorCode } = data;

          let errorMessage = '';

          switch (errorCode) {
            case 'E1000':
              errorMessage = 'This email already exists.';
              break;
            case 'E1001':
              this.isShowAlert = true;
              errorMessage = 'This email is invalid.';
              break;
            default:
              errorMessage = 'An unknown error occurred.';
              break;
          }

          return this.notiErrorDefault(`Cannot sign up. ${errorMessage}`);
        }
      }

      this.$emit('completed', payload);
    },

    goToSignInPage() {
      this.$router.push({ name: 'SignIn', query: this.$route.query });
    },

    async goBackToPreviousPage() {
      sessionStorage.removeItem('kmutt4life__temp-password');

      if (getAuth().currentUser) {
        await logout();
      }

      if (this.$route.query.previousPath) {
        const { previousPath, payload } = this.$route.query;

        return this.$router.push({
          path: previousPath,
          query: {
            payload
          }
        });
      } else {
        return this.$router.go(-1);
      }
    }
  }
};
</script>

<style lang="scss">
#input__area-of-employment {
  .vs__selected-options {
    padding: 0 0.8rem !important;
  }
  .vs__dropdown-toggle {
    padding: 0.53rem 0 0.53rem 0 !important;
  }
  .vs__actions {
    padding: 0 0.8rem 0 0.5rem !important;
  }
  .vs__search {
    color: #d1d2db !important;
    margin: 0 !important;
  }
  .vs__selected {
    margin: 0 !important;
  }
}
.select__required {
  .vs__dropdown-toggle {
    padding: 0.53rem 0 0.53rem 0 !important;
    border-color: #f83245 !important;
  }
}

.text_feedback {
  width: 100%;
  margin-top: 0.25rem;
  font-size: 80%;
  color: #f83245;
}
</style>
